import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';

export interface NutritionInfo {
  caloriesPer100: number;
  proteinPer100: number;
  fatPer100: number;
  carbsPer100: number;
  fiberPer100: number;
}

export interface RecognizeEventResponse {
  type: 'food' | 'liquid' | 'workout' | 'feeling' | 'medication' | 'task' | 'unrecognized';
  name: string;
  totalAmount?: number;
  unit?: string;
  portionName?: string;
  portionSize?: number;
  portionCount?: number;
  scheduledLdt: string; // local datetime string (no Z suffix)
  originalText?: string;
  nutrition?: NutritionInfo; // Додано поле для нутрієнтів
  durationMinutes?: number; // Додано тривалість для тренувань
}

export interface RecognizeFoodDto {
  text: string;
  contextDate: string; // ISO String
  force?: boolean;
}

export interface RecognizeFoodResult {
  suggestedTitle: string;
  items: RecognizeEventResponse[];
}

export interface EstimateNutritionDto {
  items: Array<{
    name: string;
    totalAmount?: number;
    unit?: string;
  }>;
  force?: boolean;
}

export interface CreateFoodEventsBulkDto {
  startFromStep?: 1 | 2 | 3;
  items?: RecognizeEventResponse[];
  text?: string;
  force?: boolean;
  calendarId?: number;
  rawInput?: string;
  scheduledLdt?: string; // local datetime string (no Z suffix)
  title?: string;
  isCompleted?: boolean;
}

class FoodAnalyzerService {
  private readonly BASE_URL = '/ai/food-analyzer';

  async recognize(payload: RecognizeFoodDto, clientId?: string | number): Promise<RecognizeFoodResult> {
    const url = clientId
      ? `/client/${clientId}/ai/food-analyzer/extract`
      : `${this.BASE_URL}/extract`;
    const response = await api.post<ApiResponse<RecognizeFoodResult>>(
      url,
      payload
    );
    return unwrapApiResponse<RecognizeFoodResult>(response);
  }

  async estimateNutrition(payload: EstimateNutritionDto, clientId?: string | number): Promise<RecognizeEventResponse[]> {
    const url = clientId
      ? `/client/${clientId}/ai/food-analyzer/estimate-nutrition`
      : `${this.BASE_URL}/estimate-nutrition`;
    const response = await api.post<ApiResponse<RecognizeEventResponse[]>>(
      url,
      payload
    );
    return unwrapApiResponse<RecognizeEventResponse[]>(response);
  }

  async createEventsBulk(payload: CreateFoodEventsBulkDto): Promise<any[]> {
    const response = await api.post<ApiResponse<any[]>>(
      '/calendars/events/food/bulk',
      payload
    );
    return unwrapApiResponse<any[]>(response);
  }

  async createEventsComplex(payload: CreateFoodEventsBulkDto, clientId?: string | number): Promise<any> {
    const url = clientId
      ? `/client/${clientId}/calendar/events/food/complex`
      : '/calendars/events/food/complex';
    const response = await api.post<ApiResponse<any>>(url, payload);
    return unwrapApiResponse<any>(response);
  }

  async updateEventsComplex(eventId: number, payload: CreateFoodEventsBulkDto): Promise<any> {
    const response = await api.patch<ApiResponse<any>>(`/calendars/events/${eventId}/food/complex`, payload);
    return unwrapApiResponse<any>(response);
  }
}

export const foodAnalyzerService = new FoodAnalyzerService();
